// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ModalSize } from '@interfaces/shared.interfaces';
import { DealershipService } from '@services/dealership.service';
import { SharedService } from '@services/shared.service';
import { debounceTime, takeWhile } from 'rxjs/operators';
import { User, UserDealers } from '@interfaces/user.interfaces';
import { UserService } from '@services/user.service';
import { NavService } from '@services/nav.service';
import { PermissionsService } from '@services/permissions.service';
import { Router } from '@angular/router';
import { AppEvent } from '@interfaces/interfaces';
import { DocumentsService } from '@services/documents.service';
import { PddjQueue } from '@interfaces/document.interfaces';

type EventHandler = (event: AppEvent) => void;

@Component({
    selector: 'change-dealership-modal',
    templateUrl: './change-dealership-modal.component.html',
    styleUrls: ['./change-dealership-modal.component.scss'],
})
export class ChangeDealershipModalComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('searchInput') searchInput: ElementRef;

    private isComponentAlive = true;

    public title: string;
    public allowExistingUser = true;
    public allDealershipsForUser: UserDealers[] = [];
    public filteredDealershipsForUser: UserDealers[] = [];
    public totalRecords = 0;
    public dataSource = new MatTableDataSource([]);
    public isLoading = true;
    public displayedColumns: string[] = ['name', 'dealerCode'];
    public dealersForm: FormGroup;
    public user: User;
    public dealerSelection: UserDealers;
    public loadingDealerChange = false;
    public documentQueue: any[];

    constructor(
        private dialogRef: MatDialogRef<ChangeDealershipModalComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: { user: User; disableClose?: boolean; dealerSelection: UserDealers; eventHandler: EventHandler; allDealershipsForUser: UserDealers[] },
        private dealershipService: DealershipService,
        private sharedService: SharedService,
        private userService: UserService,
        private readonly navService: NavService,
        private readonly permissionsService: PermissionsService,
        private readonly router: Router,
        private readonly documentService: DocumentsService
    ) {
        this.user = this.data.user;
        this.dealerSelection = this.data.dealerSelection;
        this.dealersForm = new FormGroup({
            search: new FormControl(''),
        });
    }

    ngOnInit() {
        this.documentService.pddjDocumentQueue$.pipe(takeWhile(() => this.isComponentAlive)).subscribe((queue) => {
            this.documentQueue = queue;
        });
        this.dealersForm
            .get('search')
            .valueChanges.pipe(
                debounceTime(300) // wait for 300ms pause in events
            )
            .subscribe(() => {
                this.searchDealers();
            });

        this.allDealershipsForUser = this.data.allDealershipsForUser;
        this.searchDealers();
    }

    ngAfterViewInit(): void {
        const event: AppEvent = {
            context: 'modal-opened',
            data: {},
        };

        this.data.eventHandler(event);
    }

    searchDealers() {
        this.isLoading = true;

        if (this.dealersForm.value.search) {
            const search = this.dealersForm.value.search.toLowerCase().trim();

            this.filteredDealershipsForUser = this.allDealershipsForUser.filter(
                (d) => d?.name?.toLowerCase()?.includes(search) || d?.dealerCodeDisplay?.toLowerCase()?.includes(search)
            );
        } else {
            this.filteredDealershipsForUser = this.allDealershipsForUser;
        }

        this.totalRecords = this.filteredDealershipsForUser.length;
        this.dataSource.data = this.filteredDealershipsForUser;

        this.isLoading = false;
    }

    updateTable() {
        this.dataSource.data = this.allDealershipsForUser;
    }

    changeDealershp(dealerId: number) {
        // Update the dealerId for the user so that it can persist properly upon refresh and login
        this.loadingDealerChange = true;

        this.userService
            .updateUser$(this.user.email, this.user.firstName, this.user.lastName, this.user.phone, this.user.middleName, dealerId, this.user.defaultPageId)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((user) => {
                if (user) {
                    this.userService.setUserCurrentDealer(user.dealerId);
                }

                this.dealershipService
                    .getDealer$(dealerId)
                    .pipe(takeWhile(() => this.isComponentAlive))
                    .subscribe(async (dealer) => {
                        this.loadingDealerChange = false;
                        // update the leads nav item title based on the selected dealer OEM
                        this.navService.setLeadsNavItemTitle(dealer.oemCode);
                        if (dealer) {
                            if (this.router.url.includes('pddj')) {
                                if (this.documentQueue.length < 1) {
                                    this.router.navigate(['pddj', 'status']);
                                } else {
                                    this.router.navigate(['/pddj', 'deals']).then(() => {
                                        this.dealerSelection = this.allDealershipsForUser.find((d) => d.dealerId === dealerId);
                                    });
                                }
                            } else {
                                this.router.navigate(this.permissionsService.routeToDealerHomePage()).then(() => {
                                    this.dealerSelection = this.allDealershipsForUser.find((d) => d.dealerId === dealerId);
                                });
                            }
                        }

                        const event: AppEvent = {
                            context: 'dealer-changed',
                            data: {
                                updatedDealer: dealer,
                            },
                        };

                        this.data.eventHandler(event);

                        this.close(true);
                    });
            });
    }

    public close(success = false) {
        this.dialogRef.close(success);
    }

    public getTableHeight() {
        return 'max-height: ' + (this.sharedService.getModalHeight(ModalSize.Medium) - 215) + 'px';
    }

    showRecordCount() {
        return (!this.isLoading && this.allDealershipsForUser.length) || this.dealersForm.value.search;
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }
}
